/**
 * React Profiler Event Logger
 *
 * Handles logging of React Profiler performance events to PostHog Analytics.
 * Validates event parameters using Yup schemas before sending to ensure data quality.
 * @see {@link https://react.dev/reference/react/Profiler | React Profiler API}
 * @module features/analytics/utils/profilerEventLogger
 */
import { ANALYTICS_EVENTS } from "../constants";
import type {
  TProfilerRenderParams,
  IProfilerAnalyticsService,
} from "../types";
import { sendAnalyticsEvent } from "./posthogSender";

/**
 * Profiler events analytics logger.
 *
 * Provides methods for logging React Profiler performance metrics.
 * @example
 * ```typescript
 * import { profilerEventLogger } from '@/features/analytics';
 *
 * const handleProfilerRender = (
 *   id: string,
 *   phase: "mount" | "update" | "nested-update",
 *   actualDuration: number,
 *   baseDuration: number,
 *   startTime: number,
 *   commitTime: number
 * ) => {
 *   profilerEventLogger.logProfilerRender({
 *     id,
 *     phase,
 *     actualDuration,
 *     baseDuration,
 *     startTime,
 *     commitTime
 *   });
 * };
 * ```
 */
export const profilerEventLogger: IProfilerAnalyticsService = {
  /**
   * Logs a React Profiler render event.
   * @param params - Profiler render parameters from React's onRender callback
   */
  logProfilerRender: async (params: TProfilerRenderParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PROFILER.RENDER, params);
  },
};
